import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { BarChart3, Layers, Boxes, Archive, Gauge, Inbox, CheckCircle2 } from "lucide-react";
import { OriginSummary } from "../../lib/origin.jsx";
import { SectionHeader } from "../../components/Primitives";
import { getMeta, getOverview } from "../../lib/uploadsApi";
import OverviewTab from "../../components/console/OverviewTab";
import BronzeTab from "../../components/console/BronzeTab";
import SilverTab from "../../components/console/SilverTab";
import GoldTab from "../../components/console/GoldTab";
import ReadinessTab from "../../components/console/ReadinessTab";
import DataLifecycleTab from "../../components/lifecycle/DataLifecycleTab";
import { LAYER } from "../../components/pipeline/Layer";
import { cn } from "../../lib/utils";

const TABS = [
  { id: "overview", label: "Overview", icon: BarChart3 },
  { id: "bronze", label: "Bronze", icon: Inbox, layer: "Bronze" },
  { id: "silver", label: "Silver", icon: Layers, layer: "Silver" },
  { id: "gold", label: "Gold", icon: Boxes, layer: "Gold" },
  { id: "readiness", label: "Model Readiness", icon: Gauge },
  { id: "lifecycle", label: "Data Lifecycle", icon: Archive },
];

function TabBar({ tab, onTab, waiting }) {
  return (
    <div className="tile p-1.5 flex flex-wrap gap-1" data-testid="dm-tabs">
      {TABS.map(t => {
        const Icon = t.icon;
        const on = tab === t.id;
        return (
          <button key={t.id} onClick={() => onTab(t.id)} data-testid={`dm-tab-${t.id}`}
                  className={cn("inline-flex items-center gap-2 px-4 h-9 rounded-sm text-sm transition-colors", on ? "bg-primary text-primary-fg font-semibold" : "text-muted hover:bg-surface-2 hover:text-fg")}>
            {t.layer && <span className={cn("w-1.5 h-1.5 rounded-full", LAYER[t.layer].dot)} />}<Icon size={14} />{t.label}
            {t.id === "bronze" && waiting > 0 && <span data-testid="bronze-badge" className={cn("inline-flex items-center justify-center min-w-[18px] h-4 px-1 rounded-full text-[10px] font-semibold font-mono-tabular", on ? "bg-primary-fg/20 text-primary-fg" : "bg-primary text-primary-fg")}>{waiting}</span>}
          </button>);
      })}
    </div>
  );
}

export default function DataManagement() {
  const [params, setParams] = useSearchParams();
  const tab = TABS.some(t => t.id === params.get("tab")) ? params.get("tab") : "overview";
  const item = params.get("item");
  const [meta, setMeta] = useState(null);
  const [waiting, setWaiting] = useState(0);
  const [nonce, setNonce] = useState(0);
  const [toast, setToast] = useState(null);
  const onToast = useCallback((m) => { setToast(m); setTimeout(() => setToast(t => (t === m ? null : t)), 3200); }, []);
  const changed = useCallback(() => setNonce(n => n + 1), []);
  useEffect(() => { getMeta().then(setMeta).catch(e => onToast(e.message)); }, [onToast]);
  useEffect(() => { getOverview().then(o => setWaiting(o.bronze.waiting)).catch(() => {}); }, [nonce, tab]);
  const go = (id) => setParams({ tab: id });
  return (
    <div className="space-y-6 animate-fade-in">
      <SectionHeader eyebrow="Admin Console" title="Data Management Console"
        description="Review, clean and promote data through the layers: Bronze → Silver → Gold, then switch models onto it." />
      <OriginSummary variant="system" />
      <TabBar tab={tab} onTab={go} waiting={waiting} />
      {tab === "overview" && <OverviewTab onGo={go} nonce={nonce} />}
      {tab === "bronze" && (meta ? <BronzeTab meta={meta} initialItem={item} onToast={onToast} onChanged={changed} /> : <div className="tile p-6 text-sm text-muted">Loading Bronze…</div>)}
      {tab === "silver" && <SilverTab onToast={onToast} onChanged={changed} />}
      {tab === "gold" && <GoldTab onToast={onToast} onChanged={changed} />}
      {tab === "readiness" && <ReadinessTab />}
      {tab === "lifecycle" && <DataLifecycleTab />}
      {toast && <div data-testid="dm-toast" className="fixed bottom-6 right-6 z-[80] bg-surface border border-border-strong rounded-sm shadow-lg px-4 py-3 flex items-center gap-2 text-sm max-w-md">
        <CheckCircle2 size={14} className="text-primary shrink-0" />{toast}</div>}
    </div>
  );
}
